import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert, Button, FieldError, FieldRow, Input, Label } from '@sentinel/ui';
import { totpCodeSchema, type TotpCodeInput } from '../schema';

export interface TotpChallengeStepProps {
  onSubmit: (code: string) => Promise<void> | void;
  /** Inline error such as "verify_invalid_code". */
  error?: string | null;
  submitting?: boolean;
}

export function TotpChallengeStep({ onSubmit, error, submitting }: TotpChallengeStepProps) {
  const { t } = useTranslation('mfa');
  const form = useForm<TotpCodeInput>({ resolver: zodResolver(totpCodeSchema) });

  async function handleSubmit(values: TotpCodeInput) {
    await onSubmit(values.code.trim());
  }

  const codeErrorRaw = form.formState.errors.code?.message;
  const codeError = codeErrorRaw === 'code_invalid' ? t('code_invalid') : codeErrorRaw;

  return (
    <div
      role="tabpanel"
      id="mfa-challenge-panel-totp"
      aria-labelledby="mfa-challenge-tab-totp"
      style={{ display: 'grid', gap: '1rem' }}
    >
      {error ? (
        <Alert type="error" role="alert">
          {error}
        </Alert>
      ) : null}

      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        style={{ display: 'grid', gap: '0.75rem' }}
      >
        <FieldRow>
          <Label htmlFor="mfa-verify-totp-code">{t('totp_code_label')}</Label>
          <Input
            id="mfa-verify-totp-code"
            data-testid="mfa-verify-totp-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            autoFocus
            placeholder={t('totp_code_placeholder')}
            aria-invalid={form.formState.errors.code ? 'true' : undefined}
            {...form.register('code')}
          />
          <FieldError message={codeError} />
        </FieldRow>
        <Button
          type="submit"
          data-testid="mfa-verify-totp-submit"
          disabled={Boolean(submitting) || form.formState.isSubmitting}
        >
          {t('totp_submit')}
        </Button>
      </form>
    </div>
  );
}
